import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreateOrderInput,
  Rep,
  SampleOrderDetail,
  SampleOrderListResponse,
  SampleOrderProduct,
  SampleOrderStats,
  SampleOrderSummary,
  Stage,
  TransitionResponse,
} from './types'

const KEY = ['sample-orders'] as const

export function useSampleOrderProducts(q = '') {
  return useQuery({
    queryKey: [...KEY, 'products', q],
    queryFn: () =>
      jsonFetch<{ products: SampleOrderProduct[] }>(
        `/api/sample-orders/search/products?q=${encodeURIComponent(q)}`,
      ),
    staleTime: 30_000,
  })
}

/** SPI sales reps (inside + outside) for the Owner dropdown — derived live
 * from RBAC roles server-side, not a separately-curated list. */
export function useSampleOrderReps() {
  return useQuery({
    queryKey: [...KEY, 'reps'],
    queryFn: () => jsonFetch<{ reps: Rep[] }>('/api/sample-orders/reps'),
    staleTime: 5 * 60_000,
  })
}

export interface SampleOrderFilters {
  status?: Stage
  q?: string
  assigned_to?: string
  archived?: boolean
  limit?: number
  offset?: number
}

export function useSampleOrderList(filters: SampleOrderFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.q) params.set('q', filters.q)
  if (filters.assigned_to) params.set('assigned_to', filters.assigned_to)
  if (filters.archived) params.set('archived', 'true')
  params.set('limit', String(filters.limit ?? 200))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: () => jsonFetch<SampleOrderListResponse>(`/api/sample-orders/?${qs}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useSampleOrderStats() {
  return useQuery({
    queryKey: [...KEY, 'stats'],
    queryFn: () => jsonFetch<SampleOrderStats>('/api/sample-orders/stats'),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

export function useSampleOrderDetail(orderId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', orderId],
    queryFn: () => jsonFetch<SampleOrderDetail>(`/api/sample-orders/${orderId}`),
    enabled: !!orderId,
    staleTime: 10_000,
  })
}

export function useCreateSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOrderInput) =>
      jsonFetch<SampleOrderSummary>('/api/sample-orders/', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useTransitionSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      orderId,
      targetStage,
      note,
    }: {
      orderId: string
      targetStage: Stage
      note?: string
    }) =>
      jsonFetch<TransitionResponse>(`/api/sample-orders/${orderId}/transition`, {
        method: 'POST',
        body: { target_stage: targetStage, note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useBlockSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      orderId,
      blockedReason,
      blockedReasonDetail,
      note,
    }: {
      orderId: string
      blockedReason: string
      blockedReasonDetail?: string
      note?: string
    }) =>
      jsonFetch<SampleOrderSummary>(`/api/sample-orders/${orderId}/block`, {
        method: 'POST',
        body: {
          blocked_reason: blockedReason,
          blocked_reason_detail: blockedReasonDetail,
          note,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useConfirmSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderId: string) =>
      jsonFetch<SampleOrderSummary>(`/api/sample-orders/${orderId}/confirm`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useCancelSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, note }: { orderId: string; note?: string }) =>
      jsonFetch<SampleOrderSummary>(`/api/sample-orders/${orderId}/cancel`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useCloseOutSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, note }: { orderId: string; note?: string }) =>
      jsonFetch<SampleOrderSummary>(`/api/sample-orders/${orderId}/close-out`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function usePatchSampleOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      orderId,
      edits,
    }: {
      orderId: string
      edits: Record<string, unknown>
    }) =>
      jsonFetch<SampleOrderSummary>(`/api/sample-orders/${orderId}`, {
        method: 'PATCH',
        body: edits,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export interface Party {
  kind: 'company' | 'contact'
  id: number
  name: string
  company_id: number | null
  company_name: string | null
  email: string | null
}

/** Unified company-or-contact search for the New Sample Order form. */
export async function searchParties(q: string): Promise<Party[]> {
  if (!q.trim()) return []
  const res = await jsonFetch<{ parties: Party[] }>(
    `/api/sample-orders/search-parties?q=${encodeURIComponent(q)}`,
  )
  return res.parties ?? []
}

export interface ContactDetail {
  address: string
  company_id: number | null
  company_name: string | null
}

/** Fetch a contact's company (commercial parent) + formatted Odoo address. */
export async function fetchContactDetail(partnerId: number): Promise<ContactDetail> {
  const res = await jsonFetch<ContactDetail>(
    `/api/sample-orders/contact/${partnerId}/address`,
  )
  return {
    address: res.address ?? '',
    company_id: res.company_id ?? null,
    company_name: res.company_name ?? null,
  }
}
